/**
 * Sales Completeness Notice
 *
 * Indique si les ventes d'un site sont completes ou si des tickets de caisse ne sont pas encore
 * disponibles (remontee tardive de la caisse vers Bill, ou tickets recus sans detail).
 *
 * - mode "control" (Controle Ventes POS) : etat general du site, avec le detail des tickets
 * - mode "report" (Rapport de Marge) : n'apparait que si la periode du rapport est incomplete
 *
 * @see sprints/todo/sprint-completude-ventes-bill US-6
 */

import { useState } from 'react';
import { Callout } from '@bill/ui';
import { Button, Modal, ModalFooter } from '@/components/ui';
import { useCanSeeFinancials, useCurrency, useFormatDate } from '@/hooks';
import { useReceiptGaps, useSalesCompleteness } from '../hooks';
import type { PosReceiptGapItem, SalesCompletenessSite } from '@bstock/shared';

interface SalesCompletenessNoticeProps {
  mode: 'control' | 'report';
  /** Periode du rapport (mode "report"), au format des filtres de date */
  dateFrom?: string;
  dateTo?: string;
}

function formatDayFr(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function gapStatusLabel(gap: PosReceiptGapItem): string {
  if (gap.status === 'OPEN') return 'En attente de la caisse';
  if (gap.status === 'REJECTED') {
    return gap.rejectReason === 'NO_LINES' ? 'Annulation sans detail, vente d\'origine introuvable' : 'Produit absent du catalogue';
  }
  if (gap.status === 'ABSENT') return 'Inexistant chez Bill';
  return 'Recupere';
}

export function SalesCompletenessNotice({ mode, dateFrom, dateTo }: SalesCompletenessNoticeProps) {
  const showFinancials = useCanSeeFinancials();
  const currency = useCurrency();
  const { formatDateTimeShort, formatDateShort } = useFormatDate();
  const [detailSite, setDetailSite] = useState<SalesCompletenessSite | null>(null);

  const isReport = mode === 'report';
  const { data } = useSalesCompleteness(
    isReport ? { dateFrom, dateTo } : {},
    !isReport || Boolean(dateFrom && dateTo),
  );
  const gaps = useReceiptGaps({ siteId: detailSite?.brandId }, detailSite !== null);

  const formatAmount = (centimes: number) =>
    `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  const sites = data?.sites ?? [];
  if (sites.length === 0) return null;

  // ---------------------------------------------------------------------------
  // Rapport : seulement si la periode est incomplete
  // ---------------------------------------------------------------------------
  if (isReport) {
    const affected = sites.filter((s) => s.periodOpenGaps > 0 || s.periodRejectedGaps > 0);
    if (affected.length === 0) return null;

    const open = affected.reduce((sum, s) => sum + s.periodOpenGaps, 0);
    const rejected = affected.reduce((sum, s) => sum + s.periodRejectedGaps, 0);
    const rejectedAmount = affected.reduce((sum, s) => sum + s.periodRejectedAmount, 0);
    const sweeps = affected.map((s) => s.lastSweepAt).filter((d): d is string => Boolean(d)).sort();
    const lastSweep = sweeps.length > 0 ? sweeps[sweeps.length - 1] : null;

    return (
      <Callout variant="warning" title="Donnees incompletes sur cette periode">
        <ul className="list-disc pl-4 space-y-1">
          {open > 0 && (
            <li>
              {open} ticket{open > 1 ? 's' : ''} de caisse {open > 1 ? 'ne sont' : "n'est"} pas encore remonte{open > 1 ? 's' : ''} jusqu'a Bill.
              Ils seront pris en compte automatiquement des leur arrivee.
            </li>
          )}
          {rejected > 0 && (
            <li>
              {rejected} ticket{rejected > 1 ? 's' : ''} recu{rejected > 1 ? 's' : ''} sans le detail des produits
              {rejected > 1 ? ' ne sont' : " n'est"} pas deduit{rejected > 1 ? 's' : ''} du chiffre d'affaires
              {showFinancials && rejectedAmount !== 0 ? ` (${formatAmount(rejectedAmount)})` : ''}.
            </li>
          )}
        </ul>
        {affected.length > 1 && (
          <p className="mt-2 text-xs">Sites concernes : {affected.map((s) => s.brandName).join(', ')}</p>
        )}
        {lastSweep && <p className="mt-2 text-xs">Derniere verification : {formatDateTimeShort(lastSweep)}</p>}
      </Callout>
    );
  }

  // ---------------------------------------------------------------------------
  // Controle ventes : etat de chaque site
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-2">
      {sites.map((site) => {
        const hasIssues = site.openGaps > 0 || site.rejectedGaps > 0;
        // Aucun traitement nocturne encore passe sur ce site : rien de fiable a afficher.
        if (!hasIssues && !site.completeUntil) return null;

        const siteLabel = sites.length > 1 ? `${site.brandName} — ` : '';

        if (!hasIssues) {
          return (
            <Callout key={site.brandId} variant="success" title={`${siteLabel}Donnees completes jusqu'au ${formatDayFr(site.completeUntil!)}`}>
              {site.lastSweepAt && <p className="text-xs">Derniere verification : {formatDateTimeShort(site.lastSweepAt)}</p>}
            </Callout>
          );
        }

        return (
          <Callout
            key={site.brandId}
            variant="warning"
            title={`${siteLabel}Tickets de caisse a surveiller`}
            actions={
              <Button variant="secondary" size="sm" onClick={() => setDetailSite(site)}>
                Voir le detail
              </Button>
            }
          >
            <ul className="list-disc pl-4 space-y-1">
              {site.openGaps > 0 && (
                <li>
                  {site.openGaps} ticket{site.openGaps > 1 ? 's' : ''} en attente de la caisse
                  {site.oldestOpenSince ? ` (depuis le ${formatDateShort(site.oldestOpenSince)})` : ''}.
                </li>
              )}
              {site.rejectedGaps > 0 && (
                <li>
                  {site.rejectedGaps} ticket{site.rejectedGaps > 1 ? 's' : ''} recu{site.rejectedGaps > 1 ? 's' : ''} sans detail, non pris en compte.
                </li>
              )}
            </ul>
            <p className="mt-2 text-xs">
              {site.completeUntil ? `Controle quotidien OK jusqu'au ${formatDayFr(site.completeUntil)}. ` : ''}
              {site.lastSweepAt ? `Derniere verification : ${formatDateTimeShort(site.lastSweepAt)}` : ''}
            </p>
          </Callout>
        );
      })}

      <Modal
        isOpen={detailSite !== null}
        onClose={() => setDetailSite(null)}
        title={`Tickets de caisse a surveiller${detailSite && sites.length > 1 ? ` — ${detailSite.brandName}` : ''}`}
        description="Tickets detectes chaque nuit a partir de la numerotation de la caisse."
        size="3xl"
      >
        <div className="px-6 py-4">
          {gaps.isLoading ? (
            <p className="text-sm text-neutral-500">Chargement...</p>
          ) : (gaps.data ?? []).length === 0 ? (
            <p className="text-sm text-neutral-500">Aucun ticket a surveiller.</p>
          ) : (
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                  <tr>
                    <th className="py-2 pr-6 font-medium whitespace-nowrap">Ticket</th>
                    <th className="py-2 pr-6 font-medium whitespace-nowrap">Vente</th>
                    <th className="py-2 pr-6 font-medium">Statut</th>
                    {showFinancials && <th className="py-2 text-right font-medium whitespace-nowrap">Montant HT</th>}
                  </tr>
                </thead>
                <tbody>
                  {(gaps.data ?? []).map((gap) => (
                    <tr key={gap.receiptId} className="border-b border-neutral-100">
                      <td className="py-2 pr-6 font-mono text-xs whitespace-nowrap">{gap.receiptId}</td>
                      <td className="py-2 pr-6 whitespace-nowrap">
                        {gap.saleDate
                          ? formatDateTimeShort(gap.saleDate)
                          : gap.estimatedSaleDate
                            ? `vers le ${formatDateShort(gap.estimatedSaleDate)}`
                            : '—'}
                      </td>
                      <td className="py-2 pr-6">{gapStatusLabel(gap)}</td>
                      {showFinancials && (
                        <td className="py-2 text-right whitespace-nowrap">{gap.amountExclTax !== null ? formatAmount(gap.amountExclTax) : '—'}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setDetailSite(null)}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
